// shared-lib/group-customization/createLink.js
//
// Link constraint factories. A link is a typed constraint between endpoints
// stored on `groupCustomization.links` (and, from layout-relationships
// step 2, `room.links`). See reference-docs/layout-relationships/overview.md.
//
//   rp         — relative position. Directional. Dragging `sourceId` pulls
//                `targetId` so target stays at `offset` (anchor-local) from
//                source. Drawn as an arrow source → target.
//
//   copy       — symmetric, between two same-type "parents". Couples each
//                parent's RP-link children index-by-index. It does NOT bind
//                the parents' own poses. A copy whose parents have no
//                matching-type rp children expands to zero constraints —
//                legal (links may be added later) but INERT; the editor
//                shows it as such (linkSemantics.isInertLink).
//
//   mirror     — same as copy except offsets are reflected on local X.
//
//   frame      — symmetric. Two objects reflected about an axis: a free
//                `lineX` the user can drag (anchor-local X, parallel to
//                local Z), or a FIXED `axis` ref (an opening or a wall) —
//                "framing a doorway". Stored alias: `centerLine`.
//
//   rail-y     — symmetric N-ary. All `memberIds` share the same `dy`
//                (anchor-local). Drag any member's dy and the rest follow.
//                Doesn't constrain dx, dz, or rotation.
//
//   center-on  — symmetric N-ary. All members share the same `dx`. Free
//                members are object ids in `memberIds`; FIXED members
//                (an opening centre, a wall midpoint) are refs in
//                `fixedMembers`, and one of those makes the link "centered
//                on X". Stored alias: `rail-x`.
//
//   on-wall    — `subjectId`'s dz and rotYRel are pinned to the plane of
//                `wall` (a wall ref). Replaces the solver's implicit,
//                type-inferred wall projection with a stated link.
//
//   align-end  — a cabinet run's terminal edge (`runEnd`, a run-end ref)
//                lands on `target`: an opening EDGE (`edge` required), a
//                wall start/end, another run's end, or an object's edge.
//                The stated form of the auto-kitchen's endpoint snap.
//
// Stored data may carry the alias spellings; readers compare through
// `canonicalType`. `createLink` accepts either name and stores the canonical
// one.

const { normalizeRef, isFixedRef } = require('./refs');
const { canonicalType } = require('./linkSemantics');

const LINK_TYPES = [
  'rp',
  'copy',
  'mirror',
  'frame',
  'centerLine',
  'rail-y',
  'center-on',
  'rail-x',
  'on-wall',
  'align-end',
];

// Provenance of a link. 'user' = the editor drew it; 'auto' = the builder
// (e.g. emitVanityLinks) emitted it as a record of an assembly rule. The
// editor distinguishes them visually and `mergeLinks` lets user links
// survive a rebuild while replacing auto links of the same shape.
const LINK_SOURCES = ['auto', 'user'];

function genLinkId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `link-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSource(source) {
  return source === 'auto' ? 'auto' : 'user';
}

/**
 * Split a `center-on` member list into free object ids and fixed refs.
 * Accepts the legacy `memberIds` (ids only), an explicit `fixedMembers`
 * (refs), and/or a mixed `members` list of ids and refs.
 */
function splitCenterOnMembers(params) {
  const memberIds = [];
  const fixedMembers = [];
  const push = (m) => {
    const r = normalizeRef(m);
    if (!r) return;
    if (isFixedRef(r)) fixedMembers.push(r);
    else if (r.kind === 'object') memberIds.push(r.id);
    else fixedMembers.push(r); // appliance / bay / run-end: solved by param
  };
  for (const m of Array.isArray(params.memberIds) ? params.memberIds : [])
    push(m);
  for (const m of Array.isArray(params.members) ? params.members : []) push(m);
  for (const m of Array.isArray(params.fixedMembers) ? params.fixedMembers : [])
    push(m);
  return { memberIds, fixedMembers };
}

/**
 * Build a link of the given type. Caller supplies the endpoints plus
 * type-specific params; this stamps a uuid, normalises the type name and
 * fills defaults.
 *
 * @param {string} rawType — one of LINK_TYPES (aliases accepted)
 * @param {Object} params — type-specific fields plus optional `source`
 *   ('auto' | 'user', default 'user').
 */
function createLink(rawType, params) {
  if (!params) return null;
  const type = canonicalType(rawType);
  const id = genLinkId();
  const source = normalizeSource(params.source);
  // Provenance beyond `source`: the RULE that emitted the link (an auto link
  // says which decision it records — `sinkOnWindow`, `endpointSnap`) and an
  // optional relaxation priority (§3.5). Neither takes part in `linkKey`.
  const tags = {
    ...(params.rule ? { rule: String(params.rule) } : {}),
    ...(params.priority != null ? { priority: params.priority } : {}),
  };
  if (type === 'rp') {
    // rotationMode controls whether the target's rotYRel follows the
    // source's rotation: 'copy' (default — angle delta preserved) or
    // 'none' (position-only RP; target rotates independently).
    return {
      id,
      type,
      source,
      ...tags,
      sourceId: params.sourceId,
      targetId: params.targetId,
      offset: params.offset || { dx: 0, dy: 0, dz: 0 },
      rotYDiff: params.rotYDiff != null ? params.rotYDiff : 0,
      rotationMode: params.rotationMode || 'copy',
    };
  }
  if (type === 'copy' || type === 'mirror') {
    return {
      id,
      type,
      source,
      ...tags,
      parentAId: params.parentAId,
      parentBId: params.parentBId,
    };
  }
  if (type === 'frame') {
    // rotationMode controls how the partner's rotYRel is set during
    // propagation: 'copy' (same), 'mirror' (negated), or 'none'
    // (untouched — user can rotate each side independently).
    return {
      id,
      type,
      source,
      ...tags,
      objectAId: params.objectAId,
      objectBId: params.objectBId,
      lineX: params.lineX != null ? params.lineX : 0,
      axis: params.axis ? normalizeRef(params.axis) : null,
      rotationMode: params.rotationMode || 'copy',
    };
  }
  if (type === 'rail-y') {
    return {
      id,
      type,
      source,
      ...tags,
      memberIds: Array.isArray(params.memberIds) ? [...params.memberIds] : [],
    };
  }
  if (type === 'center-on') {
    const { memberIds, fixedMembers } = splitCenterOnMembers(params);
    return { id, type, source, ...tags, memberIds, fixedMembers };
  }
  if (type === 'on-wall') {
    if (!params.subjectId || !params.wall) return null;
    const wall = normalizeRef(params.wall);
    if (wall.kind !== 'wall') return null;
    return { id, type, source, ...tags, subjectId: params.subjectId, wall };
  }
  if (type === 'align-end') {
    if (!params.runEnd || !params.target) return null;
    const runEnd = normalizeRef(params.runEnd);
    const target = normalizeRef(params.target);
    if (runEnd.kind !== 'run-end') return null;
    return { id, type, source, ...tags, runEnd, target };
  }
  return null;
}

module.exports = {
  LINK_TYPES,
  LINK_SOURCES,
  createLink,
  // Exposed for tests; the editor / builder should always use createLink.
  genLinkId,
};
